/**
 * `/audit` — the forensic record.
 *
 * Read-only, and there is nothing here that could change it. The filters mirror
 * `forsys audit:tail` exactly, so a question answered at the terminal is
 * answerable here and the other way round.
 *
 * **Paging is by cursor, not offset.** The log is append-only and read
 * newest-first, so an offset page would shift under the reader as new rows
 * arrive — page three could show a row page two already showed. Each page is
 * fetched with the last id of the one before it and appended, which is also why
 * there is no "previous": going back is scrolling up.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { AuditEventDto } from '@forsys/shared';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PageHeader } from '../../components/PageHeader';
import { Timestamp } from '../../components/Timestamp';
import { useDebounced } from '../../hooks/useDebounced';
import { listAuditEvents, type AuditQuery } from '../../api/audit';

const PAGE_SIZE = 50;

/** `resource.grant.added` → colour by the part before the first dot. */
function domainColour(event: string): 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'default' {
  const domain = event.slice(0, event.indexOf('.'));
  switch (domain) {
    case 'user':
    case 'group':
    case 'role':
      return 'primary';
    case 'resource':
      return 'info';
    case 'auth':
      return 'secondary';
    case 'notification':
      return 'success';
    default:
      return 'default';
  }
}

/** The before/after pair, only for the keys that actually changed. */
function ChangeDetail({ event }: { event: AuditEventDto }) {
  const { t } = useTranslation(['audit', 'common']);
  const keys =
    event.changedKeys ??
    [...new Set([...Object.keys(event.before ?? {}), ...Object.keys(event.after ?? {})])];

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('audit:detail.correlationId')}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {event.correlationId ?? t('common:state.empty')}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('audit:detail.sourceIp')}
          </Typography>
          <Typography variant="body2">
            {event.source}
            {event.ip ? ` · ${event.ip}` : ''}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('audit:detail.entity')}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {event.entityType}:{event.entityId}
          </Typography>
        </Box>
      </Box>

      {keys.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {t('audit:detail.nothingChanged')}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '25%' }}>{t('audit:detail.field')}</TableCell>
              <TableCell>{t('audit:detail.before')}</TableCell>
              <TableCell>{t('audit:detail.after')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map((key) => (
              <TableRow key={key}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{key}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                  {JSON.stringify(event.before?.[key]) ?? t('common:state.empty')}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                  {JSON.stringify(event.after?.[key]) ?? t('common:state.empty')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {event.userAgent && (
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          {event.userAgent}
        </Typography>
      )}
    </Stack>
  );
}

export default function AuditPage() {
  const { t } = useTranslation(['audit', 'common']);
  const [filters, setFilters] = useState({
    event: '',
    entityType: '',
    entityId: '',
    actorId: '',
    correlationId: '',
    since: '',
  });

  const debounced = useDebounced(filters, 400);

  // Accumulated pages, rather than a `useAsync` per page — the list grows as
  // "Load more" is pressed and the earlier pages must stay on screen.
  const [events, setEvents] = useState<AuditEventDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  const load = useCallback(
    async (beforeId?: string) => {
      setLoading(true);
      setError(undefined);

      const query: AuditQuery = {
        limit: PAGE_SIZE,
        ...(debounced.event ? { event: debounced.event } : {}),
        ...(debounced.entityType ? { entityType: debounced.entityType } : {}),
        ...(debounced.entityId ? { entityId: debounced.entityId } : {}),
        ...(debounced.actorId && Number.isInteger(Number(debounced.actorId))
          ? { actorId: Number(debounced.actorId) }
          : {}),
        ...(debounced.correlationId ? { correlationId: debounced.correlationId } : {}),
        ...(debounced.since ? { since: new Date(debounced.since).toISOString() } : {}),
        ...(beforeId ? { beforeId } : {}),
      };

      try {
        const page = await listAuditEvents(query);
        // Appended when continuing, replaced when the filters changed.
        setEvents((current) => (beforeId ? [...current, ...page.events] : page.events));
        setCursor(page.nextBeforeId);
      } catch (cause) {
        setError(cause instanceof Error ? cause : new Error(String(cause)));
      } finally {
        setLoading(false);
      }
    },
    [debounced],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = Object.values(filters).some((value) => value !== '');

  return (
    <>
      <PageHeader
        title={t('audit:title')}
        description={t('audit:description')}
        actions={
          <>
            {filtered && (
              <Button
                startIcon={<FilterAltOffIcon />}
                onClick={() =>
                  setFilters({
                    event: '',
                    entityType: '',
                    entityId: '',
                    actorId: '',
                    correlationId: '',
                    since: '',
                  })
                }
              >
                {t('audit:clearFilters')}
              </Button>
            )}
            <Button startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>
              {t('common:action.refresh')}
            </Button>
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          }}
        >
          <TextField
            size="small"
            label={t('audit:filter.event')}
            placeholder={t('audit:filter.eventPlaceholder')}
            value={filters.event}
            onChange={(event) => setFilters({ ...filters, event: event.target.value })}
            helperText={t('audit:filter.eventHelp')}
          />
          <TextField
            size="small"
            label={t('audit:filter.entityType')}
            placeholder={t('audit:filter.entityTypePlaceholder')}
            value={filters.entityType}
            onChange={(event) => setFilters({ ...filters, entityType: event.target.value })}
          />
          <TextField
            size="small"
            label={t('audit:filter.entityId')}
            value={filters.entityId}
            onChange={(event) => setFilters({ ...filters, entityId: event.target.value })}
          />
          <TextField
            size="small"
            label={t('audit:filter.actorId')}
            value={filters.actorId}
            onChange={(event) => setFilters({ ...filters, actorId: event.target.value })}
          />
          <TextField
            size="small"
            label={t('audit:filter.correlationId')}
            value={filters.correlationId}
            onChange={(event) => setFilters({ ...filters, correlationId: event.target.value })}
            helperText={t('audit:filter.correlationHelp')}
          />
          <TextField
            size="small"
            type="datetime-local"
            label={t('audit:filter.since')}
            value={filters.since}
            onChange={(event) => setFilters({ ...filters, since: event.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
      </Paper>

      {error && <ErrorState error={error} onRetry={() => void load()} />}

      {events.length === 0 && !loading ? (
        <EmptyState
          icon={<HistoryIcon fontSize="inherit" />}
          title={t('audit:emptyTitle')}
          description={filtered ? t('audit:emptyFiltered') : t('audit:emptyPlain')}
        />
      ) : (
        <Stack spacing={1}>
          {events.map((event) => (
            <Accordion key={event.id} disableGutters variant="outlined" sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={{ xs: 0.5, md: 2 }} sx={{ alignItems: { xs: 'flex-start', md: 'center' }, width: '100%', minWidth: 0 }}
                >
                  <Chip
                    size="small"
                    color={domainColour(event.event)}
                    label={event.event}
                    sx={{ fontFamily: 'monospace', fontSize: 11 }}
                  />
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {/* The actor as they were labelled *at the time* — "user 7"
                        is useless once user 7 is renamed, and useless in a
                        different way once they are gone. */}
                    <strong>{event.actor.label}</strong>
                    {/* Who was really at the keyboard, when the actor was being
                        impersonated. Without it the row blames the target. */}
                    {event.actor.impersonator && (
                      <Typography component="span" variant="body2" color="warning.main">
                        {' '}({t('audit:impersonatedBy', { label: event.actor.impersonator.label })})
                      </Typography>
                    )}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' · '}
                      {event.entityType}:{event.entityId}
                    </Typography>
                  </Typography>
                  <Tooltip title={event.source}>
                    <Chip size="small" variant="outlined" label={event.source} />
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
                    <Timestamp value={event.occurredAt} />
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <ChangeDetail event={event} />
              </AccordionDetails>
            </Accordion>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            {loading ? (
              <CircularProgress size={24} />
            ) : cursor ? (
              <Button variant="outlined" onClick={() => void load(cursor)}>
                {t('audit:loadOlder')}
              </Button>
            ) : (
              events.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t('audit:endOfLog')}
                </Typography>
              )
            )}
          </Box>
        </Stack>
      )}
    </>
  );
}
